import { Component } from 'react'
import { ParcelContentEntry, ShippingParcel, Parcels, Asn, DecodedItem } from 'stylewhere/api'
import {
  Box,
  Icons,
  ParcelRowItemList,
  ParcelRowStatus,
  RowProps,
  SmallTagCounter,
  StatusChip
} from 'stylewhere/components'
import styled from '@emotion/styled'
import { __, T } from 'stylewhere/i18n'
import { OperationConfig, Router, ShippingOperationConfig } from 'stylewhere/shared'
import {
  hasChecklist,
  getAsnParcelStatusChip,
  showToastError,
  getOtherParcelsReadingsByProduct
} from 'stylewhere/utils'
import { ShippingExtensions } from 'stylewhere/extensions'

interface Props extends RowProps {
  parcel: ShippingParcel
  expandable: boolean
  operation: OperationConfig
  asn?: Asn
  otherParcelsReadings?: DecodedItem<string>[]
  onRowHeightChange?: () => void
  canDelete?: boolean
  onDelete?: (id: string, type: 'parcel') => void
  filterParams?: Record<string, any>
}

interface State {
  entries?: ParcelContentEntry[]
  expanded: boolean
  loading: boolean
}

export class ParcelRow extends Component<Props, State> {
  state: State = {
    loading: false,
    entries: undefined,
    expanded: false
  } as any

  componentDidMount() {
    //this._init()
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.expanded !== this.state.expanded) {
      this.props.onRowHeightChange?.()
    }
  }

  _init = () => {
    this.loadContent()
  }

  getLabel = (product) => {
    let label = ''
    if (product) {
      if (
        product.style &&
        ((product.style.description && product.style.description != null) ||
          (product.style.value && product.style.value != null))
      )
        label +=
          product.style.description && product.style.description != null
            ? product.style.description
            : product.style.value
      if (product.material)
        label +=
          (label !== '' ? ' / ' : '') +
          (product.material.description && product.material.description != null
            ? product.material.description
            : product.material.value)
      if (product.color)
        label +=
          (label !== '' ? ' / ' : '') +
          (product.color.description && product.color.description != null
            ? product.color.description
            : product.color.value)
      if (product.size)
        label +=
          (label !== '' ? ' / ' : '') +
          (product.size.description && product.size.description != null ? product.size.description : product.size.value)
    }
    return label !== '' ? label : '---'
  }

  isItems = () => {
    const { operation } = this.props
    return operation.readingsDisplayMode === 'item'
  }

  onClick = () => {
    const { operation, expandable, parcel, asn, filterParams } = this.props
    if (!expandable) {
      Router.navigate(
        '/shipping/:opCode/asn/:asnId/add/:parcelId',
        { opCode: operation.code, asnId: asn ? asn.id : '', parcelId: parcel.id },
        {
          state: {
            asn: asn,
            parcel,
            filterParams
          }
        }
      )
    } else {
      const { entries, expanded } = this.state
      if (expanded) {
        this.setState({ expanded: false })
      } else {
        if (!entries) {
          this.setState({ loading: true }, this.loadContent)
        } else {
          this.setState({ expanded: true })
        }
      }
    }
  }

  loadContent = async () => {
    try {
      const { operation, parcel, asn, otherParcelsReadings } = this.props
      const parcelEntries: ParcelContentEntry[] =
        parcel.contentEntries ??
        (await Parcels.parcelContent(operation.id, parcel.id, this.isItems() ? 'item' : 'product'))

      if (asn?.checklist) {
        // Calcola le quantità già lette negli altri parcels se checklistMode === 'shipment'
        let otherParcelsReadingsByProduct: { [productCode: string]: number } = {}
        if (
          (operation as ShippingOperationConfig).options?.checklistMode === 'shipment' &&
          otherParcelsReadings &&
          otherParcelsReadings.length > 0
        ) {
          otherParcelsReadingsByProduct = getOtherParcelsReadingsByProduct(
            otherParcelsReadings,
            operation as ShippingOperationConfig,
            ShippingExtensions,
            'code'
          )
        }

        parcelEntries.forEach((p) => {
          const products = asn.checklist.checklistProducts ?? []
          const findByProduct = products.find((pr) => pr.productCode === p.product.code)
          if (findByProduct) {
            p.expected += findByProduct.quantity
          }
          const items = asn.checklist.checklistItems ?? []
          const findByItem = items.find((pr) => pr.productCode === p.product.code)
          if (findByItem) {
            p.expected += findByItem.quantity ?? 1
          }

          // Sottrai le quantità già lette negli altri parcels
          if (
            (operation as ShippingOperationConfig).options?.checklistMode === 'shipment' &&
            otherParcelsReadingsByProduct[p.product.code]
          ) {
            const alreadyRead = otherParcelsReadingsByProduct[p.product.code]
            p.expected = Math.max(0, p.expected - alreadyRead)
          }
        })
      }
      this.setState({ expanded: true, entries: parcelEntries, loading: false })
    } catch (e) {
      console.warn(e)
      showToastError('Error in reading the contents of the parcel', __(T.error.error))
    }
  }

  onDelete = (e: React.MouseEvent) => {
    const { onDelete } = this.props
    e.stopPropagation()
    if (onDelete) {
      onDelete(this.props.parcel.id, 'parcel')
    }
  }

  render() {
    const { parcel, expandable, operation, canDelete } = this.props
    const { loading, expanded, entries } = this.state
    let entryDetectedItems = 0
    let entryUnexpectedItems = 0
    let entryExpectedItems = 0
    if (entries && entries.length > 0) {
      entries.map((e) => {
        entryDetectedItems += e.detected
        entryExpectedItems += e.expected
        entryUnexpectedItems += e.unexpected
      })
    }

    const parcelEntryQuantity =
      parcel.parcelEntryQuantity && parcel.parcelEntryQuantity !== null ? parcel.parcelEntryQuantity : 0
    const parcelUnexpectedQuantity =
      parcel.parcelUnexpectedQuantity && parcel.parcelUnexpectedQuantity !== null ? parcel.parcelUnexpectedQuantity : 0
    const checklistExpectedQuantity = hasChecklist(operation)
      ? parcel.checklistExpectedQuantity && parcel.checklistExpectedQuantity !== null
        ? parcel.checklistExpectedQuantity
        : 0
      : undefined

    const detectedItems =
      entryDetectedItems > 0 && entryDetectedItems !== parcelEntryQuantity ? entryDetectedItems : parcelEntryQuantity
    const unexpectedItems =
      entryUnexpectedItems > 0 && entryUnexpectedItems !== parcelUnexpectedQuantity
        ? entryUnexpectedItems
        : parcelUnexpectedQuantity
    const expectedItems = checklistExpectedQuantity
      ? entryExpectedItems > 0 && entryExpectedItems !== checklistExpectedQuantity
        ? entryExpectedItems
        : checklistExpectedQuantity
      : undefined
    return (
      <Box flex mb={15} pl={0} pr={0}>
        <SessionRow onClick={() => this.onClick()} row vcenter>
          <Box row vcenter flex>
            <Box width={25} vcenter>
              <ParcelRowStatus loading={loading} expandable={expandable} expanded={expanded} />
            </Box>
            <Session row flex vcenter>
              <Box width={350}>{parcel.code}</Box>
              <Box flex={1}>
                <StatusChip status={parcel.status} customStatusType={getAsnParcelStatusChip(parcel.status)} />
              </Box>
              <Box ml={10}>
                <SmallTagCounter
                  alignSelf={'start'}
                  detected={detectedItems}
                  expected={expectedItems}
                  unexpected={unexpectedItems}
                />
              </Box>
              {canDelete && (
                <Box ml={10}>
                  <Icons.Trash width={24} height={24} onClick={this.onDelete} />
                </Box>
              )}
            </Session>
          </Box>
        </SessionRow>
        {expanded && expandable && (
          <ParcelRowItemList entries={entries} parcel={parcel} operation={operation} viewHeader={true} />
        )}
      </Box>
    )
  }
}

const SessionRow = styled(Box)`
  border-radius: 10px;
  background-color: #f6f6f6;
  padding: 15px 20px;
  margin-bottom: 10px;
`

const Session = styled(Box)`
  font-weight: 700;
  font-size: 26px;
  line-height: 30px;
  color: #454545;
  margin-left: 7px;
  justify-content: space-between;
`
